import { useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import type { RadioContract } from '@/mocks/superAdminContracts';
import { getContractStatusLabel, getContractStatusColor } from '@/mocks/superAdminContracts';
import { getPlanColor } from '@/mocks/superAdminPlans';

interface Props {
  contract: RadioContract;
  onClose: () => void;
  onNavigate: (path: string) => void;
  onSign: (c: RadioContract) => void;
  onSendForSignature: (c: RadioContract) => void;
  onCancel: (c: RadioContract) => void;
  onRenew: (c: RadioContract) => void;
}

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function fmtFull(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ContractDetailModal({ contract, onClose, onNavigate, onSign, onSendForSignature, onCancel, onRenew }: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const s = contract.status;
  const planColor = getPlanColor(contract.planId);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', h); };
  }, [onClose]);

  const handleCancelClick = () => {
    Swal.fire({
      icon: 'warning', title: '¿Cancelar contrato?', text: `${contract.contractNumber} — ${contract.radioName}`,
      showCancelButton: true, confirmButtonText: 'Cancelar contrato', cancelButtonText: 'Volver',
      confirmButtonColor: '#ef4444', cancelButtonColor: '#d1d5db',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl whitespace-nowrap', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700 whitespace-nowrap' },
      buttonsStyling: false,
    }).then((r) => { if (r.isConfirmed) { onCancel(contract); onClose(); } });
  };

  const handleRenewClick = () => {
    onRenew(contract);
    onClose();
  };

  const handleSendForSignatureClick = () => {
    onSendForSignature(contract);
    onClose();
  };

  const handleSignClick = () => {
    onSign(contract);
  };

  const hasSig = !!contract.signedAt;

  return (
    <div ref={overlayRef} className="fixed inset-0 z-[70] flex items-start justify-center p-4 pt-10 pb-10 bg-black/50 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}>
      <div className="relative w-full max-w-[780px] bg-background-50 rounded-2xl shadow-2xl animate-[fadeIn_0.2s_ease-out]" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200 sticky top-0 bg-background-50 rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
              <i className="ri-mail-send-line text-foreground-600 text-lg"></i>
            </div>
            <div>
              <p className="text-foreground-800 text-sm font-bold">{contract.contractNumber}</p>
              <p className="text-foreground-400 text-[10px]">{contract.radioName} · v{contract.contractVersion}</p>
            </div>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ml-1 ${getContractStatusColor(s)}`}>
              {getContractStatusLabel(s)}
            </span>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* ═══════ SECCIÓN 1: DOCUMENTO ═══════ */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-file-text-line text-foreground-600 text-sm"></i>
              </div>
              <h3 className="text-foreground-800 text-xs font-bold uppercase tracking-wider">Documento</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { label: 'Número', value: contract.contractNumber },
                { label: 'Radio', value: contract.radioName },
                { label: 'Frecuencia RFW', value: contract.frequency },
                { label: 'Plan', value: contract.planName, dot: planColor },
                { label: 'Versión', value: `v${contract.contractVersion}` },
                { label: 'Storage', value: contract.storageIncluded },
                { label: 'Dominio propio', value: contract.customDomain || 'No' },
                { label: 'Generado', value: fmtFull(contract.generatedAt) },
                { label: 'Vigencia', value: contract.expiresAt ? fmtFull(contract.expiresAt) : 'Indefinida' },
              ].map((r) => (
                <div key={r.label} className="bg-background-100/70 rounded-xl p-3 border border-background-200/70">
                  <p className="text-foreground-400 text-[9px] font-medium uppercase tracking-wider">{r.label}</p>
                  <p className="text-foreground-800 text-xs font-semibold mt-0.5 flex items-center gap-1.5">
                    {r.dot && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.dot }}></span>}
                    {r.value}
                  </p>
                </div>
              ))}
            </div>

            {/* Cláusulas */}
            <div className="mt-3 bg-background-100/70 rounded-xl p-4 border border-background-200/70">
              <p className="text-foreground-400 text-[9px] font-medium uppercase tracking-wider mb-2">Cláusulas ({contract.clauses.length})</p>
              <div className="flex flex-wrap gap-1.5 max-h-[120px] overflow-y-auto">
                {contract.clauses.map((cid, i) => (
                  <span key={cid} className="px-2 py-0.5 rounded-full bg-background-200/70 text-foreground-500 text-[10px] font-mono">{cid.replace('cl_', '#')}</span>
                ))}
              </div>
            </div>

            {s === 'cancelled' && contract.cancellationReason && (
              <div className="mt-3 bg-red-50 rounded-xl p-3 border border-red-200">
                <p className="text-red-600 text-[10px] font-medium">Motivo: {contract.cancellationReason}</p>
              </div>
            )}
            {s === 'needs_revision' && contract.revisionReason && (
              <div className="mt-3 bg-orange-50 rounded-xl p-3 border border-orange-200">
                <p className="text-orange-600 text-[10px] font-medium">Revisión: {contract.revisionReason}</p>
              </div>
            )}
          </section>

          {/* ═══════ SECCIÓN 2: FIRMANTES ═══════ */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-quill-pen-line text-foreground-600 text-sm"></i>
              </div>
              <h3 className="text-foreground-800 text-xs font-bold uppercase tracking-wider">Firmantes</h3>
            </div>

            <div className="bg-background-100/70 rounded-xl border border-background-200/70 p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Proveedor - always signed */}
                <div className="text-center space-y-2 pb-4 border-b border-background-200 sm:border-b-0 sm:border-r sm:pr-4">
                  <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
                    <i className="ri-building-line text-emerald-500 text-lg"></i>
                  </div>
                  <p className="text-foreground-400 text-[9px] uppercase tracking-wider">Proveedor</p>
                  <p className="text-foreground-800 text-xs font-bold">RadioFrecuenciaWeb</p>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
                    <i className="ri-check-double-line text-emerald-500 text-[10px]"></i>
                    <span className="text-emerald-600 text-[10px] font-semibold">Firmado</span>
                  </div>
                </div>

                {/* Cliente */}
                <div className="text-center space-y-2">
                  <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${hasSig ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                    <i className={`${hasSig ? 'ri-user-star-line text-emerald-500' : 'ri-user-line text-amber-500'} text-lg`}></i>
                  </div>
                  <p className="text-foreground-400 text-[9px] uppercase tracking-wider">Cliente</p>
                  <p className="text-foreground-800 text-xs font-bold">{contract.radioName}</p>
                  {hasSig ? (
                    <div className="space-y-1">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
                        <i className="ri-check-double-line text-emerald-500 text-[10px]"></i>
                        <span className="text-emerald-600 text-[10px] font-semibold">Firmado</span>
                      </div>
                      <p className="text-foreground-600 text-[10px] font-medium">{contract.signedByName}</p>
                      <p className="text-foreground-400 text-[9px]">{contract.signedByEmail}</p>
                      <p className="text-foreground-400 text-[9px]">{fmtFull(contract.signedAt)}</p>
                      {contract.signedByIdentifier && (
                        <p className="text-foreground-300 text-[9px]">Doc: {contract.signedByIdentifier}</p>
                      )}
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200">
                      <i className="ri-time-line text-amber-500 text-[10px]"></i>
                      <span className="text-amber-600 text-[10px] font-semibold">
                        {s === 'draft' ? 'Borrador' : s === 'pending_signature' ? 'Pendiente' : s === 'needs_revision' ? 'Requiere nueva firma' : 'No firmado'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Signature line */}
              <div className="mt-5 pt-4 border-t border-background-200">
                <div className="relative h-20 bg-background-50 rounded-xl border border-background-200 flex items-center justify-center overflow-hidden">
                  {hasSig ? (
                    <div className="text-center">
                      <p className="text-foreground-300 text-3xl italic opacity-60" style={{ fontFamily: "'Dancing Script', cursive" }}>{contract.signedByName}</p>
                      <div className="absolute inset-x-0 bottom-3 border-t border-background-300 mx-12"></div>
                    </div>
                  ) : (
                    <div className="text-center space-y-1">
                      <div className="w-32 h-px bg-background-300 mx-auto mt-6"></div>
                      <p className="text-foreground-300 text-[9px]">Firma pendiente</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Sign action for pending / needs_revision */}
              {(s === 'pending_signature' || s === 'needs_revision') && (
                <div className="mt-4 flex justify-center">
                  <button onClick={handleSignClick} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-quill-pen-line text-sm"></i>
                    Firmar contrato
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* ═══════ SECCIÓN 3: FACTURACIÓN ═══════ */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-foreground-600 text-sm"></i>
              </div>
              <h3 className="text-foreground-800 text-xs font-bold uppercase tracking-wider">Facturación</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-background-100/70 rounded-xl p-3 border border-background-200/70">
                <p className="text-foreground-400 text-[9px] font-medium uppercase tracking-wider">Mensualidad</p>
                <p className="text-foreground-800 text-sm font-bold mt-0.5">{contract.monthlyFee === 0 ? 'Gratis' : `$${contract.monthlyFee}`}</p>
              </div>
              <div className="bg-background-100/70 rounded-xl p-3 border border-background-200/70">
                <p className="text-foreground-400 text-[9px] font-medium uppercase tracking-wider">Licencia Anual</p>
                <p className="text-foreground-800 text-sm font-bold mt-0.5">{contract.annualLicense === 0 ? 'Gratis' : `$${contract.annualLicense}`}</p>
              </div>
              <div className="bg-background-100/70 rounded-xl p-3 border border-background-200/70">
                <p className="text-foreground-400 text-[9px] font-medium uppercase tracking-wider">Storage</p>
                <p className="text-foreground-800 text-sm font-bold mt-0.5">{contract.storageIncluded}</p>
              </div>
              <div className="bg-background-100/70 rounded-xl p-3 border border-background-200/70">
                <p className="text-foreground-400 text-[9px] font-medium uppercase tracking-wider">Invoice</p>
                {contract.invoiceId ? (
                  <button onClick={() => onNavigate('/sa/invoices')} className="text-primary-500 text-sm font-bold mt-0.5 hover:underline cursor-pointer whitespace-nowrap">
                    #INV-{contract.invoiceId} <i className="ri-external-link-line text-[10px]"></i>
                  </button>
                ) : (
                  <p className="text-foreground-300 text-sm mt-0.5">Pendiente</p>
                )}
              </div>
            </div>
          </section>

          {/* ═══════ SECCIÓN 4: HISTORIAL ═══════ */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-history-line text-foreground-600 text-sm"></i>
              </div>
              <h3 className="text-foreground-800 text-xs font-bold uppercase tracking-wider">Historial</h3>
            </div>

            <div className="bg-background-100/70 rounded-xl border border-background-200/70 p-4 space-y-3">
              <div className="space-y-0">
                {[
                  { icon: 'ri-file-add-line', label: 'Contrato generado', date: fmtFull(contract.generatedAt), done: true },
                  { icon: hasSig ? 'ri-check-double-line' : 'ri-quill-pen-line', label: hasSig ? 'Firmado por el cliente' : 'Firma del cliente', date: hasSig ? fmtFull(contract.signedAt) : 'Pendiente', done: hasSig },
                  { icon: contract.invoiceId ? 'ri-file-list-3-line' : 'ri-file-list-3-line', label: contract.invoiceId ? 'Invoice generada' : 'Invoice', date: contract.invoiceId ? `#INV-${contract.invoiceId}` : 'Pendiente', done: !!contract.invoiceId },
                  { icon: 'ri-play-circle-line', label: 'Activación de radio', date: hasSig && contract.invoiceId ? 'Completado' : 'Pendiente', done: hasSig && !!contract.invoiceId },
                ].map((ev, i) => (
                  <div key={i} className="flex items-start gap-3 pb-3 relative">
                    {i < 3 && <div className="absolute left-[11px] top-8 w-px h-full bg-background-300"></div>}
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${ev.done ? 'bg-foreground-950 text-background-50' : 'bg-background-200 text-foreground-400'}`}>
                      <i className={`${ev.icon} text-[10px]`}></i>
                    </div>
                    <div>
                      <p className={`text-xs font-medium ${ev.done ? 'text-foreground-800' : 'text-foreground-400'}`}>{ev.label}</p>
                      <p className="text-foreground-300 text-[10px]">{ev.date}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-background-200">
                <span className="px-2 py-0.5 rounded-full bg-background-200 text-foreground-500 text-[9px] font-mono">v{contract.contractVersion}</span>
                <span className="text-foreground-400 text-[10px]">
                  {s === 'draft' ? 'Borrador activo' :
                   s === 'pending_signature' ? 'Enviado para firma' :
                   s === 'signed' ? 'Vigente' :
                   s === 'needs_revision' ? 'Requiere revisión' :
                   s === 'expired' ? 'Expirado' :
                   'Cancelado'}
                </span>
              </div>
            </div>
          </section>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-background-200 sticky bottom-0 bg-background-50 rounded-b-2xl gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => onNavigate('/sa/radios')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-radio-line text-xs"></i>Ver radio
            </button>
            <button onClick={() => onNavigate('/sa/invoices')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-file-list-3-line text-xs"></i>Ver invoice
            </button>
            <button onClick={() => onNavigate('/sa/payments')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-bank-card-line text-xs"></i>Ver pago
            </button>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Draft → Send for signature */}
            {s === 'draft' && (
              <button onClick={handleSendForSignatureClick} className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-send-plane-line text-sm"></i>Enviar para firma
              </button>
            )}

            {/* Expired → Renew */}
            {s === 'expired' && (
              <button onClick={handleRenewClick} className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-loop-left-line text-sm"></i>Renovar contrato
              </button>
            )}

            {/* Cancel (not for cancelled/expired) */}
            {s !== 'cancelled' && s !== 'expired' && (
              <button onClick={handleCancelClick} className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-red-50 text-red-500 text-xs font-medium hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap border border-red-200">
                <i className="ri-close-circle-line text-sm"></i>Cancelar
              </button>
            )}

            <button onClick={onClose} className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}